"use client"
import { useState, useEffect, useRef } from "react"
import medicalConsulting from "../../assets/images/MedicalConsulting.webp";
import accommodation from "../../assets/images/Accommodation.jpeg";
import vipService from "../../assets/images/vip-service.jpeg";
import medicalSupport from "../../assets/images/MedicalSupport.webp";
import aftercareService from "../../assets/images/AftercareService.jpg";
import styles from "./YellowServices.module.css"

export default function YellowServices() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [visibleCount, setVisibleCount] = useState(2)
  const [leftPad, setLeftPad] = useState(165)
  const [rightPad, setRightPad] = useState(120)
  const [isDragging, setIsDragging] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [dragX, setDragX] = useState(0)
  const [offsetX, setOffsetX] = useState(0)
  const dragStartXRef = useRef(0)
  const trackRef = useRef(null)
  const containerRef = useRef(null)
  const [tileWidth, setTileWidth] = useState(0)
  const GAP = 16

  const cards = [
    { 
      title: "Medical Consulting", 
      img: medicalConsulting,
      description: "A dedicated health counselor will be personally assigned to you, offering expert guidance and answering any questions you may have."
    },
    { 
      title: "Accommodation", 
      img: accommodation,
      description: "Your medical journey includes a stay at a luxury 5-star hotel, meticulously chosen to provide a tranquil and elegant retreat for your comfort."
    },
    { 
      title: "VIP Transfers", 
      img: vipService,
      description: "Enjoy the convenience and privacy of chauffeured vehicles, ensuring a comfortable and seamless transition between all your appointments."
    },
    { 
      title: "Medical Support", 
      img: medicalSupport,
      description: "We handle all your medical necessities, from prescriptions to supplies, ensuring they are readily available when you need them."
    },
    { 
      title: "Aftercare Service", 
      img: aftercareService,
      description: "To ensure optimal results and a smooth recovery, you will receive a personalized aftercare program tailored specifically to your needs."
    },
  ]

  const slides = cards.map((card, i) => ({ ...card, id: i }))

  // İlk görünüm için çerçeve dolguları (solda boşluk ve sağda kısmi görünüm)
  // Görüş alanına göre dolguları ve görünür kutucuk sayısını dinamik olarak uyarlayın
  useEffect(() => {
    const updateLayout = () => {
      const w = window.innerWidth
      if (w <= 600) {
        setVisibleCount(1)
        setLeftPad(24)
        setRightPad(24)
      } else if (w <= 900) {
        setVisibleCount(1)
        setLeftPad(48)
        setRightPad(48)
      } else if (w <= 1200) {
        setVisibleCount(2)
        setLeftPad(96)
        setRightPad(72)
      } else {
        setVisibleCount(2)
        setLeftPad(165)
        setRightPad(120)
      }
    }
    updateLayout()
    window.addEventListener("resize", updateLayout)
    return () => window.removeEventListener("resize", updateLayout)
  }, [])

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length)
  }

  // Kutu genişliğini konteynere göre hesapla (2 tam kutu görünür)
  useEffect(() => {
    const calc = () => {
      const el = containerRef.current
      if (!el) return
      const w = el.getBoundingClientRect().width - leftPad - rightPad
      const per = (w - (visibleCount - 1) * GAP) / visibleCount
      setTileWidth(per)
    }
    calc()
    window.addEventListener("resize", calc)
    return () => window.removeEventListener("resize", calc)
  }, [visibleCount, leftPad, rightPad])

  const baseOffset = -(tileWidth + GAP) // öndeki önceki kutuyu gizle

  const getWindowSlides = () => {
    const len = slides.length
    const prevIdx = (currentIndex - 1 + len) % len
    const arr = [slides[prevIdx]]
    for (let i = 0; i < visibleCount; i++) {
      const idx = (currentIndex + i) % len
      arr.push(slides[idx])
    }
    const nextIdx = (currentIndex + visibleCount) % len
    arr.push(slides[nextIdx])
    return arr
  }

  // Sürükleme işleyicileri (işaretçi olayları)
  const onPointerDown = (e) => {
    if (isAnimating) return
    setIsDragging(true)
    setDragX(0)
    dragStartXRef.current = e.clientX ?? (e.touches && e.touches[0] && e.touches[0].clientX) ?? 0
  }

  useEffect(() => {
    if (!isDragging) return
    const onMove = (e) => {
      const clientX = e.clientX ?? (e.touches && e.touches[0] && e.touches[0].clientX)
      if (clientX == null) return
      setDragX(clientX - dragStartXRef.current)
    }
    const onUp = () => {
      const threshold = 50
      const track = trackRef.current
      if (!track) return
      let snapDelta = 0
      if (dragX <= -threshold) {
        // sonraki slayta git
        snapDelta = -(tileWidth + GAP)
        setIsAnimating(true)
        track.style.transition = "transform 300ms ease"
        setOffsetX(snapDelta)
        const onEnd = () => {
          track.style.transition = "none"
          nextSlide()
          setOffsetX(0)
          setIsAnimating(false)
          track.removeEventListener("transitionend", onEnd)
        }
        track.addEventListener("transitionend", onEnd)
      } else if (dragX >= threshold) {
        // önceki slayta git
        snapDelta = tileWidth + GAP
        setIsAnimating(true)
        track.style.transition = "transform 300ms ease"
        setOffsetX(snapDelta)
        const onEnd = () => {
          track.style.transition = "none"
          prevSlide()
          setOffsetX(0)
          setIsAnimating(false)
          track.removeEventListener("transitionend", onEnd)
        }
        track.addEventListener("transitionend", onEnd)
      }

      setIsDragging(false)
      setDragX(0)
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    window.addEventListener("touchmove", onMove, { passive: true })
    window.addEventListener("touchend", onUp)
    return () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      window.removeEventListener("touchmove", onMove)
      window.removeEventListener("touchend", onUp)
    }
  }, [isDragging, dragX, tileWidth, GAP])

  return (
    <section className={`section ${styles.yellow}`}>
      <div className="container">
        <h2 className="centerTitle">
          <span>Expertise.</span> <span>Empathy.</span> <span>Exceptional Results.</span>
        </h2>
        <div className={styles.fullBleed}>
          <div className={styles.sliderWrapper}>
          <div
            ref={containerRef}
            className={styles.sliderContent}
            onPointerDown={onPointerDown}
            style={{ cursor: isDragging ? "grabbing" : "grab", paddingLeft: leftPad, paddingRight: rightPad }}
          >
            <div
              ref={trackRef}
              className={styles.sliderTrack}
              style={{
                transform: `translateX(${baseOffset + offsetX + dragX}px)`,
              }}
            >
              {getWindowSlides().map((slide, i) => (
                <div
                  className={styles.sliderTile}
                  key={`${currentIndex}-${i}`}
                  style={{
                    width: tileWidth
                      ? `${tileWidth}px`
                      : `calc((100% - ${(visibleCount - 1) * GAP}px) / ${visibleCount})`,
                  }}
                >
                  <article className={styles.serviceCard}>
                    <img src={slide.img || "/placeholder.svg"} alt={slide.title} draggable={false} />
                    <div className={styles.cardBody}>
                      <h3>{slide.title}</h3>
                      <p>{slide.description}</p>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  )
}
